import { useEffect } from 'react';
import Link from 'next/link';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import CompanyBrainCTA from '@/components/modules/CompanyBrain/CompanyBrainCTA';
import CompanyBrainHero from '@/components/modules/CompanyBrain/CompanyBrainHero';
import HowItWorks from '@/components/modules/CompanyBrain/HowItWorks';
import SafeByDesign from '@/components/modules/CompanyBrain/SafeByDesign';
import TheKnowledgeProblem from '@/components/modules/CompanyBrain/TheKnowledgeProblem';
import UseCases from '@/components/modules/CompanyBrain/UseCases';

const CompanyBrain = () => {
  // Force light mode for marketing pages
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  return (
    <div className="bg-white">
      <NextSeo
        title="Company Brain — Connect Institutional Knowledge"
        description="Connect docs, wikis, and tribal know-how into a Company Brain everyone can ask. Cited answers for HR, support, legal, sales & ops — private by design."
        canonical="https://chat.co/company-brain"
        openGraph={{
          type: 'website',
          url: 'https://chat.co/company-brain',
          title: 'Your institutional knowledge, accessible to everyone | Chat.co',
          description:
            'Build a Company Brain from your policies, wikis, and SOPs, with source citations and authenticated access for your team.',
          images: [
            {
              url: 'https://chat.co/images/og-default.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co Company Brain — institutional knowledge for everyone',
            },
          ],
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Home', item: 'https://chat.co' },
          { position: 2, name: 'Company Brain', item: 'https://chat.co/company-brain' },
        ]}
      />
      <Header />
      <CompanyBrainHero />
      <TheKnowledgeProblem />
      <HowItWorks />
      <UseCases />
      <section className="mx-auto max-w-4xl px-6 py-10 text-center">
        <h2 className="text-2xl font-semibold">Plan your internal knowledge base</h2>
        <p className="mt-4 text-slate-600">
          Compare employee workflows, access requirements, and source preparation in our{' '}
          <Link href="/internal-ai-knowledge-base" className="text-primary-600 underline">
            internal AI knowledge base guide
          </Link>
          . Review{' '}
          <Link href="/product" className="text-primary-600 underline">
            product facts and limitations
          </Link>{' '}
          before choosing a deployment.
        </p>
      </section>
      <SafeByDesign />
      <CompanyBrainCTA />
      <Footer />
    </div>
  );
};

export default CompanyBrain;
